import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { useState, useEffect, useRef } from "react";
import { postRequest } from "../../services/mainApp.service";
import { useSelector } from "react-redux";
import { Loader2, Check, ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { CustomerCombobox } from "./CustomerCombobox ";
import { Area } from "recharts";
import { message } from "antd";
import { DineInCoverDetail, PickupCustomerDetail } from "../../utils/PosSettingConstants";
import { useCheckPosSetting } from "../../customHooks/PosCustomHooks";
import {
  CASHIER, CALL_CENTER_ADMIN,
  AGENT
} from "../../common/SetupMasterEnum";

export function NewOrderDialog({ open, onOpenChange, onSubmit
  , popupTitle = "New Order", orderMasterId = null, preselectedOrderMode = null, initialData = null }) {
  const userData = useSelector((state) => state.authReducer);
  const [selectedOrderMode, setSelectedOrderMode] = useState("");
  const [selectedOrderSource, setSelectedOrderSource] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedBranchName, setSelectedBranchName] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [landmark, setLandmark] = useState("");
  const [previousAddress, setPreviousAddress] = useState("");
  const [orderModes, setOrderModes] = useState([]);
  const [orderSources, setOrderSources] = useState([]);
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  const [customerSearchResults, setCustomerSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customerNameList, setCustomerNameList] = useState([]);
  const [addressList, setAddressList] = useState([]);
  const [areaList, setAreasList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerSaved, setCustomerSaved] = useState(false);
  const [phoneId, setPhoneId] = useState();
  const [selectedArea, setSelectedArea] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [customerData, setCustomerData] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedPhoneId, setSelectedPhoneId] = useState(null);
  const [addressData, setAddressData] = useState([]);
  const [selectedCustomerAddressId, setSelectedCustomerAddressId] = useState(null);
  const [areas, setAreas] = useState([]);
  const [cities, setCities] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [allOrderMode_OrderSource, setAllOrderMode_OrderSource] = useState([]);
  const [tables, setTables] = useState([]);
  const [waiters, setWaiters] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState("");
  const [selectedWaiterId, setSelectedWaiterId] = useState("");
  const [cover, setCover] = useState("");
  const [loadingTablesWaiters, setLoadingTablesWaiters] = useState(false);
  const DineInCoverDetailSetting = useCheckPosSetting(DineInCoverDetail);
  const PickupCustomerDetailSetting = useCheckPosSetting(PickupCustomerDetail);
  const [deliveryCharges, setDeliveryCharges] = useState(0);
  const [minimumOrder, setMinimumOrder] = useState(0);
  const [branchComboboxOpen, setBranchComboboxOpen] = useState(false);
  const [branchSearch, setBranchSearch] = useState("");
  const branchComboboxRef = useRef(null);
  const [branchComboboxWidth, setBranchComboboxWidth] = useState(0);
  const [filteredAreas, setFilteredAreas] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [allAreasWithCity, setAllAreasWithCity] = useState([]);

  useEffect(() => {
    if (open) {
      fetchOrderModesAndSources();
      if (userData.userBranchList?.length === 1) {
        setSelectedBranch(userData.userBranchList[0].BranchId?.toString() ?? "");
        setSelectedBranchName(userData.userBranchList[0].BranchName);
      }
      // Only reset selectedOrderMode if no preselectedOrderMode is provided
      if (!preselectedOrderMode) {
        setSelectedOrderMode("");
      }
    } else {
      // Reset when dialog closes
      setSelectedOrderMode("");
    }
  }, [open, preselectedOrderMode]);

  // Manage branch combobox width
  useEffect(() => {
    if (branchComboboxOpen && branchComboboxRef.current) {
      setBranchComboboxWidth(branchComboboxRef.current.offsetWidth || 0);
    }
  }, [branchComboboxOpen]);

  // Update branch search when selected branch changes
  useEffect(() => {
    if (selectedBranch && userData.userBranchList) {
      const branch = userData.userBranchList.find(b => b.BranchId?.toString() === selectedBranch);
      if (branch) {
        setBranchSearch(branch.BranchName);
      }
    } else {
      setBranchSearch("");
    }
  }, [selectedBranch, userData.userBranchList]);

  // Update delivery charges when area changes
  useEffect(() => {
    if (selectedArea && areas.length > 0) {
      const selectedAreaData = areas.find(area =>
        area.AreaId.toString() === selectedArea.toString()
      );
      if (selectedAreaData) {
        setDeliveryCharges(selectedAreaData.DeliveryCharges || 0);
        setMinimumOrder(selectedAreaData.MinimumOrder || 0);
      }
    } else {
      setDeliveryCharges(0);
      setMinimumOrder(0);
    }
  }, [selectedArea, areas]);

  // Filter areas and branches based on selected city
  useEffect(() => {
    if (selectedCity && allAreasWithCity.length > 0) {
      // Filter areas that belong to the selected city
      const cityAreas = allAreasWithCity.filter(area =>
        area.CityId && area.CityId.toString() === selectedCity.toString()
      );
      setFilteredAreas(cityAreas);

      // Get unique branches from filtered areas
      const branchIds = new Set();
      const branchesMap = new Map();

      cityAreas.forEach(area => {
        if (area.BranchId && !branchIds.has(area.BranchId.toString())) {
          branchIds.add(area.BranchId.toString());
          branchesMap.set(area.BranchId.toString(), {
            BranchId: area.BranchId,
            BranchName: area.BranchName
          });
        }
      });

      // Filter branches from userBranchList that are associated with areas in the selected city
      const availableBranches = userData.userBranchList?.filter(branch => {
        // Check if any area for this city has this branch
        return cityAreas.some(area => area.BranchId?.toString() === branch.BranchId.toString());
      }) || [];

      setFilteredBranches(availableBranches);

      // Reset selected area if it doesn't belong to the selected city
      if (selectedArea) {
        const currentArea = allAreasWithCity.find(a =>
          a.AreaId.toString() === selectedArea.toString()
        );
        if (currentArea && currentArea.CityId?.toString() !== selectedCity.toString()) {
          setSelectedArea("");
          setDeliveryCharges(0);
          setMinimumOrder(0);
          // Only reset branch if it's not in the filtered branches
          if (!availableBranches.some(b => b.BranchId.toString() === selectedBranch)) {
            setSelectedBranch("");
            setSelectedBranchName("");
          }
        }
      }

      // Reset selected branch if it doesn't belong to the filtered branches
      if (selectedBranch && availableBranches.length > 0) {
        const branchExists = availableBranches.some(b => b.BranchId.toString() === selectedBranch);
        if (!branchExists) {
          setSelectedBranch("");
          setSelectedBranchName("");
        }
      }
    } else {
      // If no city is selected, show all areas and branches
      setFilteredAreas(allAreasWithCity.length > 0 ? allAreasWithCity : areaList);
      setFilteredBranches(userData.userBranchList || []);
    }
  }, [selectedCity, allAreasWithCity, areaList, userData.userBranchList, selectedArea, selectedBranch]);

  // Set preselected order mode when orderModes are loaded and filter order sources
  useEffect(() => {
    if (preselectedOrderMode && orderModes.length > 0 && open && allOrderMode_OrderSource.length > 0) {
      const mode = orderModes.find(m => m.id.toString() === preselectedOrderMode.toString());
      if (mode) {
        setSelectedOrderMode(preselectedOrderMode.toString());
        // Filter and set order sources for the preselected order mode
        const filteredSources = allOrderMode_OrderSource.filter(item =>
          item.OrderModeId.toString() === preselectedOrderMode.toString()
        );
        const sources = [...new Map(filteredSources.map(item =>
          [item.OrderSourceId, { id: item.OrderSourceId, name: item.OrderSource }]
        )).values()];

        setOrderSources(sources);
        // Auto-select if there's only one OrderSource
        if (sources.length === 1) {
          setSelectedOrderSource(sources[0].id.toString());
        }
      }
    }
  }, [preselectedOrderMode, orderModes, open, allOrderMode_OrderSource]);

  // Auto-select OrderSource if there's only one option
  useEffect(() => {
    if (orderSources.length === 1 && !selectedOrderSource) {
      setSelectedOrderSource(orderSources[0].id.toString());
    }
  }, [orderSources, selectedOrderSource]);

  useEffect(() => {
    const dineInMode = selectedOrderMode && orderModes.find(mode =>
      mode.id.toString() === selectedOrderMode && mode.name.toLowerCase().includes("dine")
    );

    if (dineInMode && selectedBranch) {
      fetchTablesAndWaiters();
    } else {
      setTables([]);
      setWaiters([]);
      setSelectedTableId("");
      setSelectedWaiterId("");
      setCover("");
    }
  }, [selectedOrderMode, selectedBranch, orderModes]);

  // Handle initial data prefilling when dialog opens
  useEffect(() => {
    if (open && initialData && orderModes.length > 0 && allOrderMode_OrderSource.length > 0) {
      if (initialData.branchId) {
        setSelectedBranch(initialData.branchId.toString());
      }
      if (initialData.branchName) {
        setSelectedBranchName(initialData.branchName);
      }
      if (initialData.customerName) {
        setCustomerName(initialData.customerName);
      }
      if (initialData.phoneNumber) {
        setPhoneNumber(initialData.phoneNumber);
      }
      if (initialData.address) {
        setAddress(initialData.address);
      }
      if (initialData.landmark) {
        setLandmark(initialData.landmark);
      }
      if (initialData.orderMode) {
        setSelectedOrderMode(initialData.orderMode.toString());
        // Filter and set order sources for the order mode
        const filteredSources = allOrderMode_OrderSource.filter(item =>
          item.OrderModeId.toString() === initialData.orderMode.toString()
        );
        const sources = [...new Map(filteredSources.map(item =>
          [item.OrderSourceId, { id: item.OrderSourceId, name: item.OrderSource }]
        )).values()];

        setOrderSources(sources);
      }
      if (initialData.orderSource) {
        setSelectedOrderSource(initialData.orderSource.toString());
      }
      if (initialData.customerId) {
        setSelectedCustomerId(initialData.customerId);
      }
      if (initialData.phoneId) {
        setPhoneId(initialData.phoneId);
        setSelectedPhoneId(initialData.phoneId);
      }
      if (initialData.customerAddressId) {
        setSelectedCustomerAddressId(initialData.customerAddressId);
      }
      if (initialData.areaId) {
        setSelectedArea(initialData.areaId.toString());
      }
      if (initialData.tableId) {
        setSelectedTableId(initialData.tableId.toString());
      }
      if (initialData.waiterId) {
        setSelectedWaiterId(initialData.waiterId.toString());
      }
    }
  }, [open, initialData, orderModes, allOrderMode_OrderSource]);

  const selectedBranchObj = userData.userBranchList?.find(
    (branch) => branch.BranchId?.toString() === selectedBranch
  );

  const fetchOrderModesAndSources = async () => {
    setLoading(true);
    try {
      const response = await postRequest("CrudOrderSource", {
        SetupDetailName: "",
        Flex1: "",
        Flex2: "",
        Flex3: "",
        OrderModeId: null,
        OperationId: 6,
        CompanyId: userData.CompanyId,
        UserId: userData.UserId,
        UserIP: userData.UserIP || "1.1.1.1"
      });

      if (response.data.Response) {

        const OrderModeOrderSourceArr = response.data.DataSet?.Table || [];

        const arr = userData.RoleId == AGENT ?
          OrderModeOrderSourceArr.filter(a => a.OrderModeFlex != 'DINE IN' && a.OrderSourceFlex != 'POS') :
          OrderModeOrderSourceArr;

        setAllOrderMode_OrderSource(arr);

        const OrderModeOrderSource = [...new Map(arr.map(item =>
          [item.OrderModeId, { id: item.OrderModeId, name: item.OrderMode }]
        )).values()];

        setOrderModes(OrderModeOrderSource);
      }
    } catch (error) {
      console.error("Error fetching order modes and sources:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTablesAndWaiters = async () => {
    if (!selectedBranch) return;

    setLoadingTablesWaiters(true);
    try {
      const response = await postRequest("CrudInitial", {
        BranchId: selectedBranch,
        OperationId: 1,
        CompanyId: userData.CompanyId,
        UserId: userData.UserId,
        UserIP: userData.UserIP || "1.1.1.1"
      });

      if (response.data?.DataSet) {
        const tablesData = response.data.DataSet.Table1 || [];
        const waitersData = response.data.DataSet.Table || [];

        setTables(tablesData);
        setWaiters(waitersData);
      }
    } catch (error) {
      console.error("Error fetching tables and waiters:", error);
    } finally {
      setLoadingTablesWaiters(false);
    }
  };

  const handleCustomerSearch = async () => {
    if (!phoneNumber) return;

    setSearchingCustomer(true);
    try {
      const response = await postRequest("CrudCustomer", {
        OperationId: 1,
        CompanyId: userData.CompanyId,
        PhoneNumber: phoneNumber,
        UserId: userData.UserId,
        UserIP: userData.UserIP || "1.1.1.1"
      });

      if (response.data.Response) {
        const table = response.data.DataSet?.Table || [];
        const table1 = response.data.DataSet?.Table1 || [];
        const table2 = response.data.DataSet?.Table2 || [];
        const table8 = response.data.DataSet?.Table8 || [];
        const table9 = response.data.DataSet?.Table9 || [];

        setCustomerData(table1);
        setCustomerNameList(table1.map(c => c.CustomerName));
        setPhoneId(table[0]?.PhoneId)

        setAddressData(table2);
        setAddressList(table2.map(a => a.CompleteAddress));

        // Set areas from Table8 (this contains the delivery charges data)
        setAreas(table8);

        const mergedAreas = [
          ...table2.map(a => ({ AreaId: a.AreaId, AreaName: a.AreaName, BranchId: a.BranchId, BranchName: a.BranchName, CityId: a.CityId })),
          ...table8.map(a => ({ ...a, CityId: a.CityId || null }))
        ];

        const mergedCities = [
          ...table2.map(a => ({ CityId: a.CityId, CityName: a.CityName })),
          ...table9
        ];

        const uniqueAreas = Array.from(new Map(mergedAreas.map(a => [a.AreaId, a])).values());
        setAreasList(uniqueAreas);

        // Store all areas with city information for filtering
        setAllAreasWithCity(uniqueAreas);

        // Filtering will be handled by the useEffect that watches selectedCity and allAreasWithCity

        setCityList(
          Array.from(new Map(mergedCities.map(c => [c.CityId, c])).values())
        );

        setCities(table9);

        if (table1.length === 1) {
          setCustomerName(table1[0].CustomerName);
          setSelectedCustomerId(table1[0].CustomerId);
        }

        if (table2.length === 1) {
          setAddress(table2[0].CompleteAddress);
          setLandmark(table2[0].LandMark || table2[0].Landmark || "");
          setSelectedCustomerAddressId(table2[0].CustomerAddressId);

          // Set area and update delivery charges when address is selected
          if (table2[0].AreaId) {
            setSelectedArea(table2[0].AreaId.toString());
            const areaData = table8.find(a => a.AreaId.toString() === table2[0].AreaId.toString());
            if (areaData) {
              setDeliveryCharges(areaData.DeliveryCharges || 0);
              setMinimumOrder(areaData.MinimumOrder || 0);

              setSelectedBranch(areaData?.BranchId.toString() || "");
              setSelectedBranchName(areaData?.BranchName || "");
            }
          }
        }
      } else {
        setCustomerSearchResults(null);
        console.log("No customer found with this phone number");
      }
    } catch (error) {
      console.error("Error searching customer:", error);
    } finally {
      setSearchingCustomer(false);
    }
  };

  const handleCustomerSelect = (customerName) => {
    setCustomerName(customerName);
    const selectedCustomer = customerData.find(c => c.CustomerName === customerName);
    if (selectedCustomer) {
      setSelectedCustomerId(selectedCustomer.CustomerId);
    } else {
      setSelectedCustomerId(null);
    }
  };

  const handleAddressSelect = (address) => {
    setAddress(address);
    const selectedAddress = addressData.find(a => a.CompleteAddress === address);

    if (selectedAddress) {
      setSelectedCustomerAddressId(selectedAddress.CustomerAddressId);
      setLandmark(selectedAddress.LandMark || selectedAddress.Landmark || "");

      const cityExists = (cityList.length > 0 ? cityList : cities)
        .some(c => c.CityId.toString() === selectedAddress.CityId?.toString());
      if (cityExists) {
        setSelectedCity(selectedAddress.CityId?.toString() || "");
      }

      const areaExists = (areaList.length > 0 ? areaList : areas)
        .some(a => a.AreaId.toString() === selectedAddress.AreaId?.toString());
      if (areaExists) {
        setSelectedArea(selectedAddress.AreaId?.toString() || "");
      }
    } else {
      setSelectedCustomerAddressId(null);
      setLandmark("");
    }
  };

  const handleAreaSelect = (areaId) => {
    setSelectedArea(areaId);

    // Update delivery charges when area is manually selected
    const areaData = areas.find(a => a.AreaId.toString() === areaId.toString());
    if (areaData) {
      setDeliveryCharges(areaData.DeliveryCharges || 0);
      setMinimumOrder(areaData.MinimumOrder || 0);
      setSelectedBranch(areaData?.BranchId.toString() || "");
      setSelectedBranchName(areaData?.BranchName || "");
    }
  };

  const handleCitySelect = (cityId) => {
    setSelectedCity(cityId || "");
    // Filtering is handled by the useEffect that watches selectedCity
  };

  const disableCreateOrderButton = () => {

    if (submitting || savingCustomer) {
      return true;
    }

    if (selectedOrderMode == "" || selectedOrderSource == "") {
      return true;
    }

    if (userData?.RoleId === AGENT) {
      if (isDeliverySelected) {
        if (customerName == "" || phoneNumber == "" || selectedArea == "" || address == "") {
          return true;
        } else {
          return false;
        }
      }
      if (isPickupSelected) {
        if (customerName == "" || phoneNumber == "" || selectedBranch == "") {
          return true;
        }
        else {
          return false;
        }
      }
    }
    else {
      if (isDineInSelected) {
        if ((selectedTableId == "" || selectedWaiterId == "")) {
          return true;
        }
        else {
          return false;
        }
      }
      if (isPickupSelected) {
        if (PickupCustomerDetailSetting && (customerName == "" || phoneNumber == "")) {
          return true;
        }
        else if (!PickupCustomerDetailSetting) {
          return false;
        }
        else {
          return false;
        }
      }
      if (isDeliverySelected) {
        if (customerName == "" || phoneNumber == "" || selectedArea == "" || address == "") {
          return true;
        }
        else {
          return false;
        }
      }
    }
    return true;
  };

  const saveCustomer = async () => {
    if (!phoneNumber || !customerName) {
      return alert("Please fill phone, name, and address before saving");
    }

    try {
      const selectedAddress = addressData.find(
        (item) =>
          item.CustomerAddressId?.toString() === selectedCustomerAddressId?.toString() ||
          item.CompleteAddress === address
      );

      const existingLandmark = (selectedAddress?.LandMark || selectedAddress?.Landmark || "").trim();
      const currentLandmark = (landmark || "").trim();
      const shouldUseLandmarkUpdateOperation =
        currentLandmark !== "" && currentLandmark !== existingLandmark;

      const payload = {
        PhoneId: null,
        PhoneNumber: phoneNumber,
        Title: "",
        CustomerName: customerName,
        IsPrimary: true,
        CustomerId: null,
        IsPrimaryAddress: true,
        AddressTypeId: null,
        CityId: selectedCity || null,
        AreaId: selectedArea || null,
        LandMark: landmark,
        CompanyName: "",
        Building: "",
        RoomHouse: "",
        BlockFloor: "",
        StreetRowLane: "",
        RoomHouseCaptionId: null,
        BlockFloorCaptionId: null,
        StreetRowLaneCaptionId: null,
        Remarks: "",
        CustomerAddressId: selectedCustomerAddressId || null,
        BranchId: selectedBranch || null,
        CompleteAddress: address,
        OperationId: (shouldUseLandmarkUpdateOperation && selectedCustomerAddressId) ? 4 : 2,
        CompanyId: userData.CompanyId,
        UserId: userData.UserId,
        UserIP: userData.UserIP || "1.1.1.1"
      };

      const response = await postRequest("CrudCustomer", payload);
      // // console.log("Save Customer Response:", response.data);

      if (response.data.Response === true && response.data.ResponseCodes === "00") {
        const table1 = response.data.DataSet?.Table1?.[0];
        if (table1) {
          // Return the saved data instead of just setting state
          return {
            PhoneId: table1.PhoneId,
            CustomerId: table1.CustomerId,
            CustomerAddressId: table1.CustomerAddressId
          };
        }
      }

      // console.log("Failed to save customer");
      return null;
    } catch (error) {
      console.error("Error saving customer:", error);
      return null;
    }
  };

  const orderTransferSubmit = async (phoneIdParam, customerIdParam, customerAddressIdParam) => {
    try {
      const payload = {
        OrderMasterId: orderMasterId,
        OrderModeId: selectedOrderMode || null,
        OrderSourceId: selectedOrderSource || null,
        BranchId: selectedBranch || null,
        AreaId: selectedArea || null,
        CompanyId: userData?.CompanyId,
        PhoneId: phoneIdParam,
        CustomerId: customerIdParam,
        CustomerAddressId: customerAddressIdParam,
        UserId: userData.UserId,
        TableId: isDineInSelected ? selectedTableId : null,
        WaiterId: isDineInSelected ? selectedWaiterId : null,
        Cover: isDineInSelected ? (cover ? parseInt(cover, 10) : 0) : null,
        OperationId: 2,
      };

      postRequest("OrderTransfer", payload)
        .then((response) => {

          if (response?.error === true) {
            message.error(response?.errorMessage);
            return;
          }
          if (response?.data?.response === false) {
            message.error(response?.DataSet?.Table?.errorMessage);
            return;
          }
          message.success(response.successMessage);

          onSubmit();

          setSelectedOrderMode("");
          setSelectedOrderSource("");
          setSelectedBranch("");
          setSelectedBranchName("");
          setCustomerName("");
          setPhoneNumber("");
          setAddress("");
          setLandmark("");
          setSelectedArea("");
          setSelectedCity("");
          setPreviousAddress("");
          setCustomerNameList([]);
          setAddressList([]);
          setCityList([]);
          setAreas([]);
          setCities([]);
          setSavingCustomer(false);
          setCustomerSaved(false);
          setSelectedTableId("");
          setSelectedWaiterId("");
          setCover("");
          setTables([]);
          setWaiters([]);
        })
        .catch((error) => { });


    } catch (error) {
      console.error("Error getting menu:", error);
      throw error;
    }
  };

  const createOrderPayloadAndSubmit = async (phoneIdParam, customerIdParam, customerAddressIdParam) => {


    try {
      const payload = {
        DisplayInPos: true,
        DisplayInWeb: false,
        DisplayInOdms: false,
        DisplayInMobile: false,
        BranchId: selectedBranch || null,
        AreaId: selectedArea,
        Barcode: "",
        CompanyId: userData?.CompanyId,
        OrderSourceId: selectedOrderSource
      };

      const response = await postRequest("CrudMenu", payload);

      const Table = response.data.DataSet?.Table || [];
      const categories = response.data.DataSet?.Table1 || [];
      const products = response.data.DataSet?.Table2 || [];
      const Table6 = response.data.DataSet?.Table6 || [];

      const Table3 = response.data.DataSet?.Table3 || [];
      const Table4 = response.data.DataSet?.Table4 || [];
      const Table11 = response.data.DataSet?.Table11 || [];
      const Table14 = response.data.DataSet?.Table14 || [];
      const Data = response.data.Data || {};

      const Table15 = response.data.DataSet?.Table15 || [];

      const selectedOrderModeObj = orderModes.find(mode => mode.id.toString() === selectedOrderMode);
      const selectedOrderSourceObj = orderSources.find(source => source.id.toString() === selectedOrderSource);
      const selectedAreaObj = areas.find(area => area.AreaId.toString() === selectedArea);

      const selectedTable = tables.find(
        (table) => table.TableId?.toString() === selectedTableId?.toString()
      );
      const selectedWaiter = waiters.find(
        (waiter) => waiter.WaiterId?.toString() === selectedWaiterId?.toString()
      );

      const discountMaster = response.data.DataSet?.Table13 || [];
      const discountDetail = response.data.DataSet?.Table14 || [];

      // Get the selected area data for delivery charges
      const selectedAreaData = areas.find(area => area.AreaId.toString() === selectedArea);

      const paymentTerms = response.data.DataSet?.Table16 || [];

      onSubmit({
        orderMode: selectedOrderMode,
        orderSource: selectedOrderSource,
        orderModeName: selectedOrderModeObj?.name || "",
        orderSourceName: selectedOrderSourceObj?.name || "",
        branchId: selectedBranch,
        customerName,
        phoneNumber,
        address,
        landmark,
        phoneId: phoneIdParam,
        customerId: customerIdParam,
        customerAddressId: customerAddressIdParam,
        areaId: selectedArea,
        cityId: selectedCity,
        areaName: selectedAreaObj?.AreaName || "",
        deliveryCharges: selectedOrderModeObj?.name?.toLowerCase() === "pickup" ? 0 : (selectedAreaData?.DeliveryCharges || 0),
        minimumOrder: selectedOrderModeObj?.name?.toLowerCase() === "pickup" ? 0 : (selectedAreaData?.MinimumOrder || 0),
        previousAddress,
        tableId: isDineInSelected ? selectedTableId : null,
        tableName: isDineInSelected ? selectedTable?.TableName || "" : "",
        waiterId: isDineInSelected ? selectedWaiterId : null,
        waiterName: isDineInSelected ? selectedWaiter?.WaiterName || "" : "",
        cover: isDineInSelected ? (cover ? parseInt(cover, 10) : 0) : null,
        categories,
        products,
        Table,
        Table6,
        Table3,
        Table4,
        Table11,
        Table14,
        Data,
        Table15,
        discountMaster,
        discountDetail,
        branchName: selectedBranchName,
        paymentTerms
      });

      // Reset form
      setSelectedOrderMode("");
      setSelectedOrderSource("");
      setSelectedBranch("");
      setSelectedBranchName("");
      setCustomerName("");
      setPhoneNumber("");
      setAddress("");
      setLandmark("");
      setSelectedArea("");
      setSelectedCity("");
      setPreviousAddress("");
      setCustomerNameList([]);
      setAddressList([]);
      setCityList([]);
      setAreas([]);
      setCities([]);
      setSavingCustomer(false);
      setCustomerSaved(false);
      setSelectedTableId("");
      setSelectedWaiterId("");
      setCover("");
      setTables([]);
      setWaiters([]);
    } catch (error) {
      console.error("Error getting menu:", error);
      throw error;
    }
  };

  const handleCreateOrder = async () => {
    setSubmitting(true);
    try {
      let finalPhoneId = null;
      let finalCustomerId = null;
      let finalCustomerAddressId = null;

      if (userData?.RoleId === AGENT) {
        if (!isDineInSelected) {
          finalPhoneId = phoneId;
          finalCustomerId = selectedCustomerId;
          finalCustomerAddressId = selectedCustomerAddressId;

          if (!(selectedCustomerId && selectedCustomerAddressId)) {
            setSavingCustomer(true);
            const savedData = await saveCustomer();
            setSavingCustomer(false);

            if (!savedData) {
              return;
            }

            finalPhoneId = savedData.PhoneId;
            finalCustomerId = savedData.CustomerId;
            finalCustomerAddressId = savedData.CustomerAddressId;
          }
          else {
            const selectedAddress = addressData.find(
              (item) =>
                item.CustomerAddressId?.toString() === selectedCustomerAddressId?.toString() ||
                item.CompleteAddress === address
            );
            const existingLandmark = (selectedAddress?.LandMark || selectedAddress?.Landmark || "").trim();
            const currentLandmark = (landmark || "").trim();
            const shouldSaveLandmarkUpdate =
              currentLandmark !== "" && currentLandmark !== existingLandmark;

            if (shouldSaveLandmarkUpdate) {
              setSavingCustomer(true);
              const savedData = await saveCustomer();
              setSavingCustomer(false);

              if (!savedData) {
                return;
              }

              finalPhoneId = savedData.PhoneId;
              finalCustomerId = savedData.CustomerId;
              finalCustomerAddressId = savedData.CustomerAddressId;
            }
          }
        }
      }
      else {
        if (isPickupSelected) {
          if (PickupCustomerDetailSetting) {
            finalPhoneId = phoneId;
            finalCustomerId = selectedCustomerId;
            finalCustomerAddressId = selectedCustomerAddressId;

            if (!(selectedCustomerId && selectedCustomerAddressId)) {
              setSavingCustomer(true);
              const savedData = await saveCustomer();
              setSavingCustomer(false);

              if (!savedData) {
                return;
              }

              finalPhoneId = savedData.PhoneId;
              finalCustomerId = savedData.CustomerId;
              finalCustomerAddressId = savedData.CustomerAddressId;
            }
          }
        }
        else if (isDeliverySelected) {
          finalPhoneId = phoneId;
          finalCustomerId = selectedCustomerId;
          finalCustomerAddressId = selectedCustomerAddressId;

          if (!(selectedCustomerId && selectedCustomerAddressId)) {
            setSavingCustomer(true);
            const savedData = await saveCustomer();
            setSavingCustomer(false);

            if (!savedData) {
              return;
            }

            finalPhoneId = savedData.PhoneId;
            finalCustomerId = savedData.CustomerId;
            finalCustomerAddressId = savedData.CustomerAddressId;
          }
        }
      }

      await createOrderPayloadAndSubmit(finalPhoneId, finalCustomerId, finalCustomerAddressId);
    } catch (error) {
      console.error("Error in create flow:", error);
    } finally {
      setSubmitting(false);
      setSavingCustomer(false);
    }
  };

  const handleOrderTransfer = async () => {
    if (!selectedOrderMode || !selectedOrderSource || ((showBranchSelection || isDineInSelected) && !selectedBranch)) {
      return;
    }
    if (!isDineInSelected && (!customerName || !phoneNumber || (showAddressInput && !selectedArea))) {
      return;
    }
    if (isDineInSelected && (!selectedTableId || !selectedWaiterId)) {
      return;
    }


    setSubmitting(true);
    try {
      let finalPhoneId = null;
      let finalCustomerId = null;
      let finalCustomerAddressId = null;

      if (!isDineInSelected) {
        finalPhoneId = phoneId;
        finalCustomerId = selectedCustomerId;
        finalCustomerAddressId = selectedCustomerAddressId;

        if (!(selectedCustomerId && selectedCustomerAddressId)) {
          setSavingCustomer(true);
          const savedData = await saveCustomer();
          setSavingCustomer(false);

          if (!savedData) {
            return;
          }

          finalPhoneId = savedData.PhoneId;
          finalCustomerId = savedData.CustomerId;
          finalCustomerAddressId = savedData.CustomerAddressId;
        }
      }

      await orderTransferSubmit(finalPhoneId, finalCustomerId, finalCustomerAddressId);
    } catch (error) {
      console.error("Error in create flow:", error);
    } finally {
      setSubmitting(false);
      setSavingCustomer(false);
    }
  };

  const showBranchSelection = selectedOrderMode && orderModes.find(mode =>
    mode.id.toString() === selectedOrderMode && (mode.name.toLowerCase() === "pickup" || mode.name.toLowerCase() === "delivery")
  );

  const isDineInSelected = selectedOrderMode && orderModes.find(mode =>
    mode.id.toString() === selectedOrderMode && mode.name.toLowerCase().includes("dine")
  );

  const showAddressInput = selectedOrderMode && !orderModes.find(mode =>
    mode.id.toString() === selectedOrderMode && (mode.name.toLowerCase() === "pickup" || mode.name.toLowerCase().includes("dine"))
  );

  const isPickupSelected = selectedOrderMode && orderModes.find(mode =>
    mode.id.toString() === selectedOrderMode && mode.name.toLowerCase() === "pickup"
  );

  const isDeliverySelected = selectedOrderMode && orderModes.find(mode =>
    mode.id.toString() === selectedOrderMode && mode.name.toLowerCase() === "delivery"
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[100vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-center">
            {popupTitle}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center items-center h-40">
            <p>Loading order options...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {!preselectedOrderMode && (
              <div>
                <Label className="text-lg font-medium mb-2 block">
                  Order Mode *
                </Label>
                <div className="flex flex-wrap gap-2">
                  {orderModes.map((mode) => (
                    <Button
                      className="p-10 text-md font-semibold"
                      key={mode.id}
                      variant={selectedOrderMode === mode.id.toString() ? "default" : "outline"}
                      onClick={() => {

                        setSelectedOrderMode(mode.id.toString());
                        const filteredSources = allOrderMode_OrderSource.filter(item =>
                          item.OrderModeId.toString() === mode.id.toString()
                        );
                        const sources = [...new Map(filteredSources.map(item =>
                          [item.OrderSourceId, { id: item.OrderSourceId, name: item.OrderSource }]
                        )).values()];
                        setOrderSources(sources);
                        // Auto-select if there's only one OrderSource
                        if (sources.length === 1) {
                          setSelectedOrderSource(sources[0].id.toString());
                        } else {
                          setSelectedOrderSource("");
                        }
                      }}>
                      {mode.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {orderSources.length > 1 && (
              <div>
                <Label className="text-lg font-medium mb-2 block">
                  Order Source *
                </Label>
                <div className="flex flex-wrap gap-2">
                  {orderSources.map((source) => (
                    <Button
                      className="p-10 text-md font-semibold"
                      key={source.id}
                      variant={selectedOrderSource === source.id.toString() ? "default" : "outline"}
                      onClick={() => setSelectedOrderSource(source.id.toString())}>
                      {source.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}



            {!isDineInSelected && !isPickupSelected && userData?.RoleId != AGENT && (
              <div className="flex items-center content-center w-full gap-3">
                <div className="relative w-1/2">
                  <Label htmlFor="phoneNumber" className="text-lg font-medium">
                    Phone Number *
                  </Label>
                  <Input
                    id="phoneNumber"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    onBlur={handleCustomerSearch}
                    placeholder="Enter phone number"
                    className="mt-1"
                    disabled={searchingCustomer}
                  />
                  {searchingCustomer && (
                    <div className="absolute right-2 top-11">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  )}
                </div>
                <div className="w-1/2">
                  <Label htmlFor="area" className="text-lg font-medium">
                    Customer *
                  </Label>
                  <CustomerCombobox
                    value={customerName}
                    onChange={handleCustomerSelect}
                    options={customerNameList}
                    placeholder="Select or type customer name"
                  />
                </div>
              </div>
            )}

            {(userData?.RoleId === AGENT || (isPickupSelected && PickupCustomerDetailSetting)) && (
              <div className="flex items-center content-center w-full gap-3">
                <div className="relative w-1/2">
                  <Label htmlFor="phoneNumber" className="text-lg font-medium">
                    Phone Number *
                  </Label>
                  <Input
                    id="phoneNumber"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    onBlur={handleCustomerSearch}
                    placeholder="Enter phone number"
                    className="mt-1"
                    disabled={searchingCustomer}
                  />
                  {searchingCustomer && (
                    <div className="absolute right-2 top-11">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  )}
                </div>
                <div className="w-1/2">
                  <Label htmlFor="area" className="text-lg font-medium">
                    Customer *
                  </Label>
                  <CustomerCombobox
                    value={customerName}
                    onChange={handleCustomerSelect}
                    options={customerNameList}
                    placeholder="Select or type customer name"
                  />
                </div>
              </div>
            )}



            {isDineInSelected && (
              <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                <Label className="text-lg font-medium mb-2 block">
                  Dine-In Details
                </Label>
                {loadingTablesWaiters ? (
                  <div className="flex justify-center items-center h-32">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="table" className="text-lg font-medium">
                          Table *
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {tables.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              No tables found for this branch.
                            </p>
                          ) : (
                            tables.map((table) => (

                              <Button
                                key={table.TableId}
                                type="button"
                                className="min-w-[120px] justify-center"
                                variant={selectedTableId === table.TableId.toString() ? "default" : "outline"}
                                disabled={table.IsOpen == true ? true : loadingTablesWaiters}
                                onClick={() => setSelectedTableId(table.TableId.toString())}
                              >
                                {table.TableName}
                              </Button>
                            ))
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="waiter" className="text-lg font-medium">
                          Waiter *
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {waiters.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              No waiters found for this branch.
                            </p>
                          ) : (
                            waiters.map((waiter) => (
                              <Button
                                key={waiter.WaiterId}
                                type="button"
                                className="min-w-[120px] justify-center"
                                variant={selectedWaiterId === waiter.WaiterId.toString() ? "default" : "outline"}
                                disabled={loadingTablesWaiters}
                                onClick={() => setSelectedWaiterId(waiter.WaiterId.toString())}
                              >
                                {waiter.WaiterName}
                              </Button>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                    {DineInCoverDetailSetting && (
                      <div className="space-y-2">
                        <Label htmlFor="cover" className="text-lg font-medium">
                          Cover
                        </Label>
                        <Input
                          id="cover"
                          type="number"
                          min="0"
                          value={cover}
                          onChange={(e) => setCover(e.target.value)}
                          placeholder="Enter number of covers"
                          className="w-full"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="space-y-4">

              <div className="flex items-center justify-center gap-3">
                {showAddressInput && cities.length > 0 && (
                  <div className="space-y-2 w-1/2">
                    <Label htmlFor="area" className="text-lg font-medium">
                      Select City
                    </Label>
                    {/* <Select value={selectedCity} onValueChange={setSelectedCity}> */}
                    <Select value={selectedCity} onValueChange={handleCitySelect}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a City" />
                      </SelectTrigger>
                      <SelectContent>
                        {cityList.length > 0 ? (
                          cityList.map((city) => (
                            <SelectItem key={city.CityId} value={city.CityId.toString()}>
                              {city.CityName}
                            </SelectItem>
                          ))
                        ) : (
                          cities.map((city) => (
                            <SelectItem key={city.CityId} value={city.CityId.toString()}>
                              {city.CityName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {showAddressInput && areas.length > 0 && (
                  <div className="space-y-2 w-1/2">
                    <Label htmlFor="area" className="text-lg font-medium">
                      Select Area *
                    </Label>
                    <Select value={selectedArea} onValueChange={handleAreaSelect}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select an area" />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          const areaOptions = selectedCity ? filteredAreas : (filteredAreas.length > 0 ? filteredAreas : areaList.length > 0 ? areaList : areas);
                          return areaOptions.length > 0 ? (
                            areaOptions.map((area) => (
                              <SelectItem key={area.AreaId} value={area.AreaId.toString()}>
                                {area.AreaName}
                                {areas.find(a => a.AreaId === area.AreaId)?.DeliveryCharges &&
                                  ` (Rs. ${areas.find(a => a.AreaId === area.AreaId)?.DeliveryCharges})`
                                }
                              </SelectItem>
                            ))
                          ) : (
                            <div className="py-6 px-4 text-center text-sm text-muted-foreground">
                              {selectedCity ? "No areas available for this city" : "No areas available"}
                            </div>
                          );
                        })()}
                      </SelectContent>
                    </Select>

                  </div>
                )}
              </div>

              {showAddressInput && (
                <div>
                  <Label htmlFor="area" className="text-lg font-medium">
                    Address *
                  </Label>
                  <CustomerCombobox
                    value={address}
                    onChange={handleAddressSelect}
                    options={addressList}
                    placeholder="Select or type Address"
                  />
                </div>
              )}
              {showAddressInput && (
                <div>
                  <Label htmlFor="landmark" className="text-lg font-medium">
                    Landmark
                  </Label>
                  <Input
                    id="landmark"
                    value={landmark}
                    onChange={(e) => setLandmark(e.target.value)}
                    placeholder="Enter landmark"
                    className="mt-1"
                  />
                </div>
              )}
            </div>

            {((showBranchSelection || isDineInSelected) && userData.userBranchList?.length > 1) && (
              <div>

                <Label className="text-lg font-medium mb-2 block">
                  Select Branch *
                </Label>

                {userData?.RoleId === AGENT ? (
                  <Popover open={branchComboboxOpen} onOpenChange={setBranchComboboxOpen}>
                    <PopoverTrigger asChild>
                      <div
                        ref={branchComboboxRef}
                        role="combobox"
                        aria-expanded={branchComboboxOpen}
                        className={cn(
                          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer"
                        )}
                      >
                        <span className="truncate">
                          {selectedBranchName ? (
                            selectedBranchName
                          ) : (
                            <span className="text-muted-foreground">Select a branch</span>
                          )}
                        </span>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </div>
                    </PopoverTrigger>
                    <PopoverContent align="start" sideOffset={4} className="z-50 p-0" style={{ width: branchComboboxWidth || undefined }}>
                      <Command>
                        <CommandInput
                          value={branchSearch}
                          onValueChange={setBranchSearch}
                          placeholder="Search branches..."
                        />
                        <CommandList>
                          <CommandEmpty>No branches found.</CommandEmpty>
                          <CommandGroup>
                            {(filteredBranches.length > 0 ? filteredBranches : userData.userBranchList)
                              .filter((branch) =>
                                branch.BranchName.toLowerCase().includes(branchSearch.toLowerCase())
                              )
                              .map((branch) => (
                                <CommandItem
                                  key={branch.BranchId}
                                  value={branch.BranchName}
                                  onSelect={() => {
                                    setSelectedBranch(branch.BranchId.toString());
                                    setSelectedBranchName(branch.BranchName);
                                    setBranchComboboxOpen(false);
                                  }}
                                >
                                  {branch.BranchName}
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {(filteredBranches.length > 0 ? filteredBranches : userData.userBranchList).map((branch) => (
                      <Button
                        className="p-10 text-md font-semibold"
                        key={branch.BranchId}
                        variant={selectedBranch === branch.BranchId.toString() ? "default" : "outline"}
                        onClick={() => {
                          setSelectedBranch(branch.BranchId.toString());
                          setSelectedBranchName(branch.BranchName);
                        }}>
                        {branch.BranchName}
                      </Button>
                    ))}
                  </div>
                )}

              </div>

            )}

            <div className="flex justify-end gap-2 pt-4">
              {popupTitle === "New Order" && (
                <Button
                  onClick={handleCreateOrder}
                  disabled={disableCreateOrderButton()}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2">
                  {savingCustomer || submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> {savingCustomer ? 'Saving...' : 'Creating...'}
                    </>
                  ) : customerSaved ? (
                    <>
                      <Check className="h-4 w-4 text-green-500" /> Saved
                    </>
                  ) : (
                    "Create Order"
                  )}
                </Button>
              )}

              {popupTitle !== "New Order" && (
                <Button
                  onClick={handleOrderTransfer}
                  disabled={
                    submitting || savingCustomer ||
                    ((showBranchSelection || isDineInSelected) && !selectedBranch) ||
                    !selectedOrderMode ||
                    !selectedOrderSource ||
                    (!isDineInSelected && (!customerName || !phoneNumber || (showAddressInput && !address))) ||
                    (isDineInSelected && (!selectedTableId || !selectedWaiterId))
                  }
                  className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2">
                  {savingCustomer || submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> {savingCustomer ? 'Saving...' : 'Creating...'}
                    </>
                  ) : customerSaved ? (
                    <>
                      <Check className="h-4 w-4 text-green-500" /> Saved
                    </>
                  ) : (
                    "Order Transfer"
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog >
  );
}
